<template>
  <AuthLayout :mode="mode">
    <Section
      heading="Edit your own Searches"
      description="Use this page to delete or edit your currently saved searches. "
      :mode="mode"
    />
    <AppCard :mode="mode">
      <template #body>
        <div class="mb-6 flex items-center justify-between gap-4 sm:flex-col">
          <div class="flex-1 sm:w-full">
            <div class="relative flex items-center">
              <Search
                :size="20"
                :stroke-width="1.75"
                class="absolute left-4 opacity-40"
              />
              <AppInput
                :mode="mode"
                type="search"
                placeholder="Search by name"
                classes="!pl-11"
                v-model="searchQuery"
                :hasLabel="false"
              />
            </div>
          </div>
        </div>
        <template v-if="filteredSearches.length > 0">
          <AppTable :mode="mode" :columns="columns">
            <template #rows>
              <tr v-for="(search, key) in filteredSearches" :key="key">
                <td>
                  <FallbackText :value="search.search_name" />
                </td>
                <td>
                  <FallbackText :value="search.days_to_search" />
                </td>
                <td>
                  <FallbackText :value="search.query" />
                </td>
                <td>False</td>
                <td>
                  <div class="flex items-center gap-2">
                    <TableAction
                      :mode="mode"
                      @itemAction="
                        $redirectTo('search-details', search.contact_search_id)
                      "
                      action="view"
                    />
                    <TableAction
                      :mode="mode"
                      @itemAction="editSearch(search)"
                      action="edit"
                    />
                    <TableAction
                      :mode="mode"
                      @itemAction="deleteSearch(search.contact_search_id)"
                      action="delete"
                    />
                  </div>
                </td>
              </tr>
            </template>
          </AppTable>
          <MobileTable
            :mode="mode"
            :fields="{
              name: 'Name',
              days: 'Days to search',
              query: 'Query',
              last: 'Last sent',
              actions: 'Actions',
            }"
            :data="filteredSearches"
          >
            <template #default="{ row, fieldKey }">
              <template v-if="fieldKey === 'name'">
                <FallbackText :value="row.search_name" :isTruncate="false" />
              </template>
              <template v-else-if="fieldKey === 'days'">
                <FallbackText :value="row.days_to_search" :isTruncate="false" />
              </template>
              <template v-else-if="fieldKey === 'query'">
                <FallbackText :value="row.query" :isTruncate="false" />
              </template>
              <template v-else-if="fieldKey === 'last'"> False </template>
              <template v-else-if="fieldKey === 'actions'">
                <div class="flex items-center gap-2">
                  <TableAction
                    :mode="mode"
                    @itemAction="
                      $redirectTo('search-details', row.contact_search_id)
                    "
                    action="view"
                  />
                  <TableAction
                    :mode="mode"
                    @itemAction="editSearch(row)"
                    action="edit"
                  />
                  <TableAction
                    :mode="mode"
                    @itemAction="deleteSearch(row.contact_search_id)"
                    action="delete"
                  />
                </div>
              </template>
            </template>
          </MobileTable>
        </template>
        <EmptyData v-else :notFound="'No searches saved!'" :mode="mode" />
      </template>
    </AppCard>
    <EditSearch
      :mode="mode"
      :currentSearch="currentSearch"
      @searchUpdated="searchUpdated"
      @closeEditSearch="closeEditSearch"
    />
  </AuthLayout>
</template>

<script>
import { Search } from "lucide-vue-next";
import Section from "@C/Common/Section.vue";
import ApiRequest from "@L/ApiRequest";
import { useGlobalErrorHandler } from "@L/errorHandler";
import { useUserStore } from "@S/user";
import { useToastStore } from "@S/toast";
import { useLoaderStore } from "@S/loader";
import EmptyData from "@C/Common/EmptyData.vue";
import AppCard from "@C/Common/AppCard.vue";
import AppInput from "@C/Form/AppInput.vue";
import AppTable from "@C/Tables/AppTable.vue";
import EditSearch from "@C/Searches/EditSearch.vue";
import AuthLayout from "@A/AuthLayout.vue";
import FallbackText from "@C/Tables/FallbackText.vue";
import MobileTable from "@C/Tables/MobileTable.vue";
import TableAction from "@C/Tables/TableAction.vue";

export default {
  name: "Searches",
  components: {
    AuthLayout,
    AppCard,
    Section,
    EmptyData,
    Search,
    AppInput,
    AppTable,
    TableAction,
    EditSearch,
    FallbackText,
    MobileTable,
  },
  setup() {
    const userStore = useUserStore();
    const toastStore = useToastStore();
    const loaderStore = useLoaderStore();
    const { handleError } = useGlobalErrorHandler();
    return {
      userStore,
      toastStore,
      loaderStore,
      handleError,
    };
  },
  props: {
    mode: String,
  },
  data() {
    return {
      currentSearch: null,
      searches: [],
      searchQuery: "",
      columns: [
        {
          name: "Name",
        },
        {
          name: "Days to Search",
        },
        {
          name: "Query",
        },
        {
          name: "Last Sent",
        },
        {
          name: "Actions",
          classes: "w-[80px]",
        },
      ],
    };
  },
  mounted() {
    this.getSearches();
  },
  methods: {
    async getSearches() {
      this.loaderStore.setIsLoading(true);
      try {
        const { data } = await ApiRequest().get(`/contact_searches`);
        if (data) {
          this.searches = data.map((search) => search._source.contact_search);
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    async deleteSearch(id) {
      this.loaderStore.setIsLoading(true);
      try {
        const { data } = await ApiRequest().delete(`/contact_searches/${id}`);
        if (data) {
          this.toastStore.show("Search deleted successfully!", "success");
          this.getSearches();
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    editSearch(search) {
      this.currentSearch = search;
    },
    closeEditSearch() {
      this.currentSearch = null;
    },
    searchUpdated() {
      this.getSearches();
      this.closeEditSearch();
    },
  },
  computed: {
    filteredSearches() {
      if (!this.searchQuery) {
        return this.searches;
      }
      return this.searches.filter((search) =>
        search.search_name
          .toLowerCase()
          .includes(this.searchQuery.toLowerCase()),
      );
    },
  },
};
</script>
